import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";

import { 
  ArrowLeft, Sparkles, Loader2, BookOpen, Target, Users, 
  Award, CheckCircle, AlertTriangle, ArrowRight, Copy, 
  Crown, Lock, CreditCard, FileText, Zap, Star, ChevronRight
} from "lucide-react";

const QUESTIONS = [
  {
    id: "business_name",
    question: "What is your business name?",
    placeholder: "e.g., Acme Consulting",
    icon: Award
  },
  {
    id: "business_description",
    question: "Briefly describe what your business does.",
    placeholder: "e.g., We help small businesses streamline their operations...",
    type: "textarea",
    icon: FileText
  },
  {
    id: "target_customer",
    question: "Who is your ideal customer? (Be specific)",
    placeholder: "e.g., Small business owners with 5-50 employees who struggle with...",
    type: "textarea",
    icon: Users
  },
  {
    id: "customer_problem",
    question: "What external problem does your customer face?",
    placeholder: "e.g., They're losing time on manual processes that could be automated...",
    type: "textarea",
    icon: AlertTriangle
  },
  {
    id: "customer_internal_problem",
    question: "How does this problem make them feel? (Internal problem)",
    placeholder: "e.g., Overwhelmed, frustrated, worried they're falling behind competitors...",
    type: "textarea",
    icon: Target
  },
  {
    id: "customer_philosophical_problem",
    question: "Why is this problem fundamentally wrong? (Philosophical)",
    placeholder: "e.g., Business owners shouldn't have to choose between growth and family time...",
    type: "textarea",
    icon: BookOpen
  },
  {
    id: "your_solution",
    question: "How does your product/service solve their problem?",
    placeholder: "e.g., Our software automates 80% of administrative tasks, giving owners back 10+ hours/week...",
    type: "textarea",
    icon: Zap
  },
  {
    id: "success_outcome",
    question: "What does success look like for your customer after working with you?",
    placeholder: "e.g., They have more time, less stress, growing revenue, and actually enjoy running their business...",
    type: "textarea",
    icon: Star
  },
  {
    id: "failure_outcome",
    question: "What happens if they DON'T take action?",
    placeholder: "e.g., They'll continue burning out, losing customers to competitors, missing family events...",
    type: "textarea",
    icon: AlertTriangle
  },
  {
    id: "call_to_action",
    question: "What's the main action you want them to take?",
    placeholder: "e.g., Schedule a free consultation, Start free trial, Book a demo...",
    icon: ArrowRight
  }
];

export const StoryBrandPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [brandscript, setBrandscript] = useState(null);
  const [savedBrandscripts, setSavedBrandscripts] = useState([]);
  const [usage, setUsage] = useState(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [activeTab, setActiveTab] = useState("create");

  useEffect(() => {
    fetchUsage();
    fetchSavedBrandscripts();
    
    // Check for payment return
    const sessionId = searchParams.get("session_id");
    const success = searchParams.get("success");
    if (sessionId && success) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const fetchUsage = async () => {
    try {
      const response = await authFetch(`${API}/storybrand/usage`, {  });
      if (response.ok) {
        setUsage(await response.json());
      }
    } catch (_error) {
      console.error("Error fetching usage:", _error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedBrandscripts = async () => {
    try {
      const response = await authFetch(`${API}/storybrand/brandscripts`, {  });
      if (response.ok) {
        setSavedBrandscripts(await response.json());
      }
    } catch (_error) {
      console.error("Error fetching brandscripts:", _error);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      toast.error("Payment verification timed out. Please refresh.");
      return;
    }

    try {
      setProcessingPayment(true);
      const response = await authFetch(`${API}/storybrand/subscription/status/${sessionId}`, {
        
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.payment_status === "paid") {
          toast.success("🎉 Welcome to StoryBrand Pro! You now have unlimited access.");
          fetchUsage();
          setProcessingPayment(false);
          // Clear URL params
          window.history.replaceState({}, '', '/storybrand');
          return;
        } else if (data.status === "expired") {
          toast.error("Payment session expired. Please try again.");
          setProcessingPayment(false);
          return;
        }
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (_error) {
      setProcessingPayment(false);
      toast.error("Error checking payment status");
    }
  };

  const handleGenerate = async () => {
    // Check if user can generate
    if (!usage?.can_generate) {
      setShowUpgradeDialog(true);
      return;
    }

    // Validate all questions answered
    const unanswered = QUESTIONS.filter(q => !answers[q.id]?.trim());
    if (unanswered.length > 0) {
      toast.error(`Please answer all questions. Missing: ${unanswered[0].question.substring(0, 30)}...`);
      setCurrentStep(QUESTIONS.findIndex(q => q.id === unanswered[0].id));
      return;
    }

    setGenerating(true);
    try {
      const response = await authFetch(`${API}/storybrand/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(answers)
      });

      if (response.status === 402) {
        setShowUpgradeDialog(true);
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setBrandscript(data.brandscript);
        setActiveTab("result");
        toast.success("Your BrandScript is ready!");
        fetchUsage();
        fetchSavedBrandscripts();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to generate BrandScript");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setGenerating(false);
    }
  };

  const handleUpgrade = async () => {
    setProcessingPayment(true);
    try {
      const response = await authFetch(`${API}/storybrand/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ origin_url: window.location.origin })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        toast.error("Failed to start checkout");
        setProcessingPayment(false);
      }
    } catch (_error) {
      toast.error("An error occurred");
      setProcessingPayment(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const loadBrandscript = (bs) => {
    setBrandscript(bs.brandscript);
    setAnswers(bs.input_data || {});
    setActiveTab("result");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="w-8 h-8 border-2 border-[#333333] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const progress = ((currentStep + 1) / QUESTIONS.length) * 100;
  const currentQuestion = QUESTIONS[currentStep];

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="storybrand-page">
      {/* Header */}
      <div className="bg-[#333333] text-white">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-white/80 hover:text-white hover:bg-white/10">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center">
                  <BookOpen className="w-6 h-6 mr-2 text-[#39FF14]" />
                  StoryBrand BrandScript Generator
                </h1>
                <p className="text-white/70 text-sm">Clarify your message with the proven StoryBrand framework</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {usage?.is_subscribed ? (
                <Badge className="bg-[#39FF14] text-[#333333]">
                  <Crown className="w-3 h-3 mr-1" />
                  Pro
                </Badge>
              ) : usage?.has_free_remaining ? (
                <Badge variant="outline" className="border-white/30 text-white">
                  1 Free BrandScript
                </Badge>
              ) : (
                <Button 
                  size="sm" 
                  onClick={() => setShowUpgradeDialog(true)}
                  className="bg-[#39FF14] text-[#333333] hover:bg-[#32E010]"
                >
                  <Crown className="w-4 h-4 mr-1" />
                  Upgrade to Pro
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Explanation Card */}
        <Card className="border-[#39FF14]/30 bg-gradient-to-r from-green-50 to-emerald-50 mb-6">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#39FF14]/10 flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-5 h-5 text-[#39FF14]" />
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-1">StoryBrand BrandScript Generator</h3>
                <p className="text-sm text-neutral-600 mb-3">
                  Clarify your marketing message using the proven StoryBrand framework. Answer guided questions 
                  about your hero (customer), their problem, your solution, and the transformation you offer. 
                  AI will generate a complete BrandScript with one-liners, email sequences, and website copy.
                </p>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="px-2 py-1 bg-white rounded border border-[#39FF14]/30 text-[#39FF14]">7-Part Framework</span>
                  <span className="px-2 py-1 bg-white rounded border border-[#39FF14]/30 text-[#39FF14]">One-Liner Generator</span>
                  <span className="px-2 py-1 bg-white rounded border border-[#39FF14]/30 text-[#39FF14]">Email Sequences</span>
                  <span className="px-2 py-1 bg-white rounded border border-[#39FF14]/30 text-[#39FF14]">Website Copy</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="create">Create BrandScript</TabsTrigger>
            <TabsTrigger value="result" disabled={!brandscript}>View Result</TabsTrigger>
            <TabsTrigger value="saved">
              Saved ({savedBrandscripts.length})
            </TabsTrigger>
          </TabsList>

          {/* Create Tab */}
          <TabsContent value="create">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Question Card */}
              <div className="lg:col-span-2">
                <Card className="border-neutral-200">
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline">Question {currentStep + 1} of {QUESTIONS.length}</Badge>
                      <span className="text-sm text-neutral-500">{Math.round(progress)}% complete</span>
                    </div>
                    <Progress value={progress} className="h-2 [&>div]:bg-[#39FF14]" />
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-xl bg-[#39FF14]/10 flex items-center justify-center flex-shrink-0">
                        {currentQuestion && <currentQuestion.icon className="w-6 h-6 text-[#39FF14]" />}
                      </div>
                      <div className="flex-1">
                        <Label className="text-lg font-medium text-neutral-900">
                          {currentQuestion?.question}
                        </Label>
                        {currentQuestion?.type === "textarea" ? (
                          <Textarea
                            value={answers[currentQuestion?.id] || ""}
                            onChange={(e) => setAnswers({ ...answers, [currentQuestion.id]: e.target.value })}
                            placeholder={currentQuestion?.placeholder}
                            className="mt-3 min-h-32"
                            data-testid={`question-${currentQuestion?.id}`}
                          />
                        ) : (
                          <Input
                            value={answers[currentQuestion?.id] || ""}
                            onChange={(e) => setAnswers({ ...answers, [currentQuestion.id]: e.target.value })}
                            placeholder={currentQuestion?.placeholder}
                            className="mt-3 h-12"
                            data-testid={`question-${currentQuestion?.id}`}
                          />
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t">
                      <Button
                        variant="outline"
                        onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                        disabled={currentStep === 0}
                      >
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Previous
                      </Button>
                      
                      {currentStep < QUESTIONS.length - 1 ? (
                        <Button
                          onClick={() => setCurrentStep(currentStep + 1)}
                          className="bg-[#333333] hover:bg-[#444444] text-white"
                        >
                          Next
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      ) : (
                        <Button
                          onClick={handleGenerate}
                          disabled={generating || (!usage?.can_generate && !usage?.has_free_remaining)}
                          className="bg-[#39FF14] hover:bg-[#32E010] text-white"
                          data-testid="generate-brandscript-btn"
                        >
                          {generating ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Sparkles className="w-4 h-4 mr-2" />
                          )}
                          Generate BrandScript
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Progress Sidebar */}
              <div className="space-y-4">
                <Card className="border-neutral-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Your Progress</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {QUESTIONS.map((q, idx) => (
                        <button
                          key={q.id}
                          onClick={() => setCurrentStep(idx)}
                          className={`w-full flex items-center space-x-2 p-2 rounded-lg text-left text-sm transition-colors ${
                            idx === currentStep 
                              ? "bg-[#39FF14]/10 text-[#39FF14]" 
                              : answers[q.id] 
                                ? "bg-green-50 text-green-700" 
                                : "hover:bg-neutral-100 text-neutral-600"
                          }`}
                        >
                          {answers[q.id] ? (
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          ) : (
                            <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                              idx === currentStep ? "border-[#39FF14]" : "border-neutral-300"
                            }`} />
                          )}
                          <span className="truncate">{q.question.substring(0, 25)}...</span>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Usage Info */}
                <Card className={`border-2 ${usage?.is_subscribed ? "border-[#39FF14]/30 bg-[#39FF14]/5" : "border-neutral-200"}`}>
                  <CardContent className="p-4">
                    {usage?.is_subscribed ? (
                      <div className="text-center">
                        <Crown className="w-8 h-8 text-[#39FF14] mx-auto mb-2" />
                        <p className="font-medium text-[#333333]">StoryBrand Pro</p>
                        <p className="text-sm text-neutral-500">Unlimited BrandScripts</p>
                      </div>
                    ) : (
                      <div className="text-center">
                        {usage?.has_free_remaining ? (
                          <>
                            <Sparkles className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                            <p className="font-medium">1 Free BrandScript</p>
                            <p className="text-sm text-neutral-500 mb-3">Try it free, upgrade for more</p>
                          </>
                        ) : (
                          <>
                            <Lock className="w-8 h-8 text-neutral-400 mx-auto mb-2" />
                            <p className="font-medium">Free Trial Used</p>
                            <p className="text-sm text-neutral-500 mb-3">Upgrade for unlimited access</p>
                          </>
                        )}
                        <Button 
                          size="sm" 
                          onClick={() => setShowUpgradeDialog(true)}
                          className="w-full bg-[#39FF14] hover:bg-[#32E010] text-white"
                        >
                          Upgrade - $25/mo
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Result Tab */}
          <TabsContent value="result">
            {brandscript && (
              <div className="space-y-6">
                {/* One-Liner */}
                <Card className="border-[#39FF14]/30 bg-[#39FF14]/5">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <Badge className="bg-[#39FF14] text-white mb-3">Your One-Liner</Badge>
                        <p className="text-xl font-medium text-neutral-900">{brandscript.one_liner}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => copyToClipboard(brandscript.one_liner)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Character */}
                  <Card className="border-neutral-200">
                    <CardHeader>
                      <CardTitle className="flex items-center text-lg">
                        <Users className="w-5 h-5 mr-2 text-blue-500" />
                        The Character (Your Customer)
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <p className="text-xs text-neutral-500 uppercase">Who They Are</p>
                        <p className="text-sm">{brandscript.character?.who}</p>
                      </div>
                      <div>
                        <p className="text-xs text-neutral-500 uppercase">What They Want</p>
                        <p className="text-sm">{brandscript.character?.wants}</p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Problem */}
                  <Card className="border-neutral-200">
                    <CardHeader>
                      <CardTitle className="flex items-center text-lg">
                        <AlertTriangle className="w-5 h-5 mr-2 text-red-500" />
                        The Problem
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <p className="text-xs text-neutral-500 uppercase">The Villain</p>
                        <p className="text-sm">{brandscript.problem?.villain}</p>
                      </div>
                      <div>
                        <p className="text-xs text-neutral-500 uppercase">External</p>
                        <p className="text-sm">{brandscript.problem?.external}</p>
                      </div>
                      <div>
                        <p className="text-xs text-neutral-500 uppercase">Internal</p>
                        <p className="text-sm">{brandscript.problem?.internal}</p>
                      </div>
                      <div>
                        <p className="text-xs text-neutral-500 uppercase">Philosophical</p>
                        <p className="text-sm">{brandscript.problem?.philosophical}</p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Guide */}
                  <Card className="border-neutral-200">
                    <CardHeader>
                      <CardTitle className="flex items-center text-lg">
                        <Award className="w-5 h-5 mr-2 text-purple-500" />
                        The Guide (You)
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <p className="text-xs text-neutral-500 uppercase">Empathy</p>
                        <p className="text-sm">{brandscript.guide?.empathy}</p>
                      </div>
                      <div>
                        <p className="text-xs text-neutral-500 uppercase">Authority</p>
                        <p className="text-sm">{brandscript.guide?.authority}</p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Plan */}
                  <Card className="border-neutral-200">
                    <CardHeader>
                      <CardTitle className="flex items-center text-lg">
                        <Target className="w-5 h-5 mr-2 text-green-500" />
                        The Plan
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex items-start space-x-3">
                          <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-xs font-bold text-green-700">1</div>
                          <p className="text-sm">{brandscript.plan?.step1}</p>
                        </div>
                        <div className="flex items-start space-x-3">
                          <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-xs font-bold text-green-700">2</div>
                          <p className="text-sm">{brandscript.plan?.step2}</p>
                        </div>
                        <div className="flex items-start space-x-3">
                          <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-xs font-bold text-green-700">3</div>
                          <p className="text-sm">{brandscript.plan?.step3}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Call to Action */}
                  <Card className="border-neutral-200">
                    <CardHeader>
                      <CardTitle className="flex items-center text-lg">
                        <ArrowRight className="w-5 h-5 mr-2 text-amber-500" />
                        Call to Action
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <p className="text-xs text-neutral-500 uppercase">Direct CTA</p>
                        <p className="text-sm font-medium text-amber-700">{brandscript.call_to_action?.direct}</p>
                      </div>
                      <div>
                        <p className="text-xs text-neutral-500 uppercase">Transitional CTA</p>
                        <p className="text-sm">{brandscript.call_to_action?.transitional}</p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Success & Failure */}
                  <Card className="border-neutral-200">
                    <CardHeader>
                      <CardTitle className="flex items-center text-lg">
                        <Zap className="w-5 h-5 mr-2 text-emerald-500" />
                        Success & Stakes
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="p-3 bg-green-50 rounded-lg">
                        <p className="text-xs text-green-600 uppercase font-medium">Success</p>
                        <p className="text-sm text-green-800">{brandscript.success?.description}</p>
                        <p className="text-xs text-green-600 mt-2">Transformation: {brandscript.success?.transformation}</p>
                      </div>
                      <div className="p-3 bg-red-50 rounded-lg">
                        <p className="text-xs text-red-600 uppercase font-medium">If They Don't Act</p>
                        <p className="text-sm text-red-800">{brandscript.failure?.stakes}</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Website Headlines */}
                <Card className="border-neutral-200">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span className="flex items-center">
                        <FileText className="w-5 h-5 mr-2 text-blue-500" />
                        Website Headlines
                      </span>
                      <Button variant="outline" size="sm" onClick={() => copyToClipboard(
                        `Hero: ${brandscript.website_headlines?.hero}\n\nSubheadline: ${brandscript.website_headlines?.subheadline}\n\nValue Proposition: ${brandscript.website_headlines?.value_proposition}`
                      )}>
                        <Copy className="w-4 h-4 mr-1" />
                        Copy All
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-4 bg-neutral-50 rounded-lg">
                      <p className="text-xs text-neutral-500 uppercase mb-1">Hero Headline</p>
                      <p className="text-xl font-bold">{brandscript.website_headlines?.hero}</p>
                    </div>
                    <div className="p-4 bg-neutral-50 rounded-lg">
                      <p className="text-xs text-neutral-500 uppercase mb-1">Subheadline</p>
                      <p className="text-lg">{brandscript.website_headlines?.subheadline}</p>
                    </div>
                    <div className="p-4 bg-neutral-50 rounded-lg">
                      <p className="text-xs text-neutral-500 uppercase mb-1">Value Proposition</p>
                      <p className="text-base">{brandscript.website_headlines?.value_proposition}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Brand Story */}
                <Card className="border-neutral-200">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>Complete Brand Story</span>
                      <Button variant="outline" size="sm" onClick={() => copyToClipboard(brandscript.brand_story_paragraph)}>
                        <Copy className="w-4 h-4 mr-1" />
                        Copy
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-base leading-relaxed">{brandscript.brand_story_paragraph}</p>
                  </CardContent>
                </Card>

                {/* Email Subject Lines */}
                {brandscript.email_subject_lines && (
                  <Card className="border-neutral-200">
                    <CardHeader>
                      <CardTitle>Email Subject Lines</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {brandscript.email_subject_lines.map((line, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                            <span className="text-sm">{line}</span>
                            <Button variant="ghost" size="sm" onClick={() => copyToClipboard(line)}>
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>

          {/* Saved Tab */}
          <TabsContent value="saved">
            {savedBrandscripts.length === 0 ? (
              <Card className="border-neutral-200">
                <CardContent className="py-12 text-center">
                  <BookOpen className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                  <p className="text-neutral-500">No saved BrandScripts yet</p>
                  <p className="text-sm text-neutral-400 mt-1">Create your first BrandScript to get started</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {savedBrandscripts.map((bs) => (
                  <Card 
                    key={bs.brandscript_id} 
                    className="border-neutral-200 cursor-pointer hover:border-[#39FF14]/50 transition-colors"
                    onClick={() => loadBrandscript(bs)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-medium">{bs.business_name}</h4>
                          <p className="text-xs text-neutral-500">{new Date(bs.created_at).toLocaleDateString()}</p>
                        </div>
                        {bs.is_free && <Badge variant="outline">Free</Badge>}
                      </div>
                      <p className="text-sm text-neutral-600 line-clamp-2">{bs.brandscript?.one_liner}</p>
                      <Button variant="ghost" size="sm" className="mt-2 text-[#39FF14]">
                        View <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Upgrade Dialog */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Crown className="w-6 h-6 mr-2 text-[#39FF14]" />
              Upgrade to StoryBrand Pro
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-[#333333]">$25<span className="text-base font-normal text-neutral-500">/month</span></p>
              <p className="text-sm text-neutral-500">Unlimited BrandScripts</p>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-[#39FF14]" />
                <span className="text-sm">Unlimited BrandScript generations</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-[#39FF14]" />
                <span className="text-sm">Save and manage all your scripts</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-[#39FF14]" />
                <span className="text-sm">Website headlines & email copy</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-[#39FF14]" />
                <span className="text-sm">Export and share options</span>
              </div>
            </div>

            <Button 
              onClick={handleUpgrade}
              disabled={processingPayment}
              className="w-full h-12 bg-[#39FF14] hover:bg-[#32E010] text-white"
            >
              {processingPayment ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <CreditCard className="w-5 h-5 mr-2" />
              )}
              {processingPayment ? "Processing..." : "Subscribe Now"}
            </Button>
            
            <p className="text-xs text-center text-neutral-500">
              Secure payment via Stripe. Cancel anytime.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Processing Payment Overlay */}
      {processingPayment && searchParams.get("session_id") && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="max-w-sm">
            <CardContent className="p-6 text-center">
              <Loader2 className="w-10 h-10 text-[#39FF14] animate-spin mx-auto mb-4" />
              <p className="font-medium">Verifying your payment...</p>
              <p className="text-sm text-neutral-500">This will only take a moment</p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default StoryBrandPage;
